import { Component, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { EventResponse, EventClassResponse, EventScheduleResponse } from '../../../../../../../../@shared/models/response.module';
import { EventsService } from '../../../../../../../../@shared/services/events-service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ScheduleEditor } from '../../../manage-event/components/manage-event-schedule/components/schedule-editor/schedule-editor';
import { AQH_EVE_6, AQH_EVE_40, _AQH_EVE_41 } from '../../../../../../../../@shared/models/input.module';
import { TranslateService } from '@ngx-translate/core';
import { DeleteModal } from '../../../../../../../../@shared/components/delete-modal/delete-modal';
import { ToastService } from '../../../../../../../../@core/services/toast.service';

export type ScheduleType = 'startlist' | 'group_session' | 'individual_lesson';

export interface ScheduleTypeOption {
  value: ScheduleType;
  label: string;
  description: string;
  icon: string;
  defaultDuration: number;
  disabled?: boolean;
  comingSoon?: boolean;
}

interface EventDay {
  date: Date;
  label: string;
  shortLabel: string;
  isToday: boolean;
}

@Component({
  selector: 'app-wizard-step-schedule',
  standalone: false,
  templateUrl: './wizard-step-schedule.html',
  styleUrl: './wizard-step-schedule.css'
})
export class WizardStepSchedule {
  @Input() event: EventResponse | null = null;
  @Output() schedulesChanged = new EventEmitter<number>();

  classes: EventClassResponse[] = [];
  filteredClasses: EventClassResponse[] = [];
  schedules: Map<number, EventScheduleResponse[]> = new Map();
  loading = true;
  totalScheduleCount = 0;

  // Day tabs
  eventDays: EventDay[] = [];
  selectedDay: Date | null = null;

  showTypeSelector: number | null = null;

  scheduleTypes: ScheduleTypeOption[] = [];

  private lastLoadedEventId: number | null = null;

  constructor(
    private eventService: EventsService,
    private modalService: NgbModal,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.scheduleTypes = [
      {
        value: 'startlist',
        label: this.translateService.instant('eventWizard.schedule.typeStartList'),
        description: this.translateService.instant('eventWizard.schedule.typeStartListDesc'),
        icon: 'fa-list-ol',
        defaultDuration: 120,
        disabled: false,
        comingSoon: false
      },
      {
        value: 'group_session',
        label: this.translateService.instant('eventWizard.schedule.typeGroupSession'),
        description: this.translateService.instant('eventWizard.schedule.typeGroupSessionDesc'),
        icon: 'fa-users',
        defaultDuration: 3600,
        disabled: true,
        comingSoon: true
      },
      {
        value: 'individual_lesson',
        label: this.translateService.instant('eventWizard.schedule.typeIndividualLesson'),
        description: this.translateService.instant('eventWizard.schedule.typeIndividualLessonDesc'),
        icon: 'fa-user-clock',
        defaultDuration: 1800,
        disabled: true,
        comingSoon: true
      }
    ];
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['event'] && this.event?.event_id && this.event.event_id !== this.lastLoadedEventId) {
      this.loadData();
    }
  }

  loadData() {
    if (!this.event?.event_id) {
      this.loading = false;
      return;
    }

    this.lastLoadedEventId = this.event.event_id;
    this.generateEventDays();
    this.loadClasses();
  }

  generateEventDays() {
    if (!this.event?.start_date || !this.event?.end_date) {
      return;
    }

    const startDate = new Date(this.event.start_date);
    const endDate = new Date(this.event.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.eventDays = [];
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
      const dayDate = new Date(currentDate);
      const isToday = dayDate.getTime() === today.getTime();

      this.eventDays.push({
        date: dayDate,
        label: this.formatDayLabel(dayDate),
        shortLabel: this.formatShortDayLabel(dayDate),
        isToday: isToday
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    if (this.eventDays.length > 0) {
      const todayTab = this.eventDays.find(d => d.isToday);
      this.selectedDay = todayTab ? todayTab.date : this.eventDays[0].date;
    }
  }

  formatDayLabel(date: Date): string {
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${weekday} - ${day}.${month}.${year}`;
  }

  formatShortDayLabel(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}.${month}`;
  }

  selectDay(day: EventDay) {
    this.selectedDay = day.date;
    this.filterClassesByDay();
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  filterClassesByDay() {
    if (!this.classes || !this.selectedDay) {
      this.filteredClasses = this.classes || [];
      return;
    }

    this.filteredClasses = this.classes.filter(eventClass => {
      if (!eventClass.start_date_time) return false;
      const classDate = new Date(eventClass.start_date_time);
      return this.isSameDay(classDate, this.selectedDay!);
    });

    // Sort by start time
    this.filteredClasses.sort((a, b) => {
      const timeA = a.start_date_time ? new Date(a.start_date_time).getTime() : 0;
      const timeB = b.start_date_time ? new Date(b.start_date_time).getTime() : 0;
      return timeA - timeB;
    });
  }

  getClassCountForDay(day: EventDay): number {
    if (!this.classes) return 0;
    return this.classes.filter(eventClass => {
      if (!eventClass.start_date_time) return false;
      const classDate = new Date(eventClass.start_date_time);
      return this.isSameDay(classDate, day.date);
    }).length;
  }

  getClassStartTime(eventClass: EventClassResponse): string {
    if (!eventClass.start_date_time) return '';
    const date = new Date(eventClass.start_date_time);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }

  loadClasses() {
    const params: AQH_EVE_6 = { event_id: this.event!.event_id };
    this.eventService.getEventClasses(params).subscribe((classes) => {
      this.classes = classes || [];
      this.filterClassesByDay();
      this.loadSchedulesForClasses();
    });
  }

  loadSchedulesForClasses() {
    this.schedules.clear();
    this.totalScheduleCount = 0;

    if (this.classes.length === 0) {
      this.loading = false;
      this.schedulesChanged.emit(0);
      return;
    }

    let pending = this.classes.length;

    for (const eventClass of this.classes) {
      const params: AQH_EVE_40 = { event_class_id: eventClass.event_class_id };
      this.eventService.getSchedule(params).subscribe({
        next: (schedules) => {
          const scheduleList = schedules && Array.isArray(schedules) ? schedules : [];
          this.schedules.set(eventClass.event_class_id, scheduleList);
          this.totalScheduleCount += scheduleList.length;
          pending--;
          if (pending === 0) {
            this.loading = false;
            this.schedulesChanged.emit(this.totalScheduleCount);
          }
        },
        error: () => {
          this.schedules.set(eventClass.event_class_id, []);
          pending--;
          if (pending === 0) {
            this.loading = false;
            this.schedulesChanged.emit(this.totalScheduleCount);
          }
        }
      });
    }
  }

  getSchedulesForClass(eventClassId: number): EventScheduleResponse[] {
    return this.schedules.get(eventClassId) || [];
  }

  hasSchedules(eventClassId: number): boolean {
    const schedules = this.schedules.get(eventClassId);
    return !!schedules && schedules.length > 0;
  }

  toggleTypeSelector(eventClassId: number) {
    this.showTypeSelector = this.showTypeSelector === eventClassId ? null : eventClassId;
  }

  createScheduleWithType(eventClass: EventClassResponse, type: ScheduleTypeOption) {
    this.showTypeSelector = null;

    const existingSchedules = this.getSchedulesForClass(eventClass.event_class_id);
    const count = existingSchedules.length + 1;

    let scheduleName = count > 1 ? `${eventClass.class_name} ${count}` : eventClass.class_name;
    let scheduleType: 'startlist' | 'clinic';
    let sessionType: 'individual' | 'group';

    switch (type.value) {
      case 'startlist':
        scheduleType = 'startlist';
        sessionType = 'individual';
        break;
      case 'group_session':
        scheduleType = 'clinic';
        sessionType = 'group';
        break;
      case 'individual_lesson':
        scheduleType = 'clinic';
        sessionType = 'individual';
        break;
      default:
        scheduleType = 'startlist';
        sessionType = 'individual';
    }

    // Use class start_date_time as schedule start time if available
    const scheduleStartTime = eventClass.start_date_time
      ? new Date(eventClass.start_date_time).toISOString()
      : undefined;

    const params: _AQH_EVE_41 = {
      event_class_id: eventClass.event_class_id,
      schedule_name: scheduleName,
      schedule_type: scheduleType,
      session_type: sessionType,
      auto_generate: true,
      time_per_entry_seconds: type.defaultDuration,
      schedule_start_time: scheduleStartTime
    };

    this.eventService.createSchedule(params).subscribe({
      next: (schedule) => {
        const current = this.schedules.get(eventClass.event_class_id) || [];
        current.push(schedule);
        this.schedules.set(eventClass.event_class_id, current);
        this.totalScheduleCount++;
        this.schedulesChanged.emit(this.totalScheduleCount);
        this.openScheduleEditor(schedule, eventClass);
      }
    });
  }

  openScheduleEditor(schedule: EventScheduleResponse, eventClass: EventClassResponse) {
    const modalRef = this.modalService.open(ScheduleEditor, {
      size: 'xl',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.schedule = schedule;
    modalRef.componentInstance.eventClass = eventClass;

    modalRef.result.then((updated) => {
      if (updated) {
        this.loadSchedulesForClasses();
      }
    }).catch(() => {});
  }

  deleteSchedule(schedule: EventScheduleResponse, eventClass: EventClassResponse) {
    const modalRef = this.modalService.open(DeleteModal, { size: 'md', centered: true });
    modalRef.componentInstance.description = this.translateService.instant('eventWizard.schedule.deleteConfirm', { name: schedule.schedule_name });

    modalRef.result.then((result) => {
      if (result) {
        this.eventService.deleteSchedule({ schedule_id: schedule.schedule_id }).subscribe({
          next: () => {
            const current = this.schedules.get(eventClass.event_class_id) || [];
            const filtered = current.filter(s => s.schedule_id !== schedule.schedule_id);
            this.schedules.set(eventClass.event_class_id, filtered);
            this.totalScheduleCount--;
            this.schedulesChanged.emit(this.totalScheduleCount);
            this.toastService.success(this.translateService.instant('eventWizard.schedule.deleteSuccess'));
          },
          error: () => {
            this.toastService.error(this.translateService.instant('eventWizard.schedule.deleteError'));
          }
        });
      }
    }).catch(() => {});
  }

  formatDateTime(dateValue: string | Date | null | undefined): string {
    if (!dateValue) return '-';
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    return date.toLocaleString('nb-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getScheduleTypeLabel(schedule: EventScheduleResponse): string {
    if (schedule.session_type === 'group') return 'Group';
    if (schedule.schedule_type === 'clinic') return 'Lesson';
    return 'Start List';
  }

  getScheduleTypeIcon(schedule: EventScheduleResponse): string {
    if (schedule.session_type === 'group') return 'fa-users';
    if (schedule.schedule_type === 'clinic') return 'fa-user-clock';
    return 'fa-list-ol';
  }

  formatDuration(seconds: number): string {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${Math.floor(seconds / 60)} min`;
  }

  getEntryCount(schedule: EventScheduleResponse): number {
    return (schedule as any).entries?.length || 0;
  }
}
